import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  LogOut, Menu, Search, ChevronDown, Eye, Truck
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin');
      return;
    }
    loadOrders();
  }, [adminToken, statusFilter]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/admin/orders${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus, trackingNumber = null) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/status`,
        { status: newStatus, tracking_number: trackingNumber },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Order status updated to ${newStatus}`);
      loadOrders();
      setSelectedOrder(null);
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/10 text-yellow-500',
      paid: 'bg-green-500/10 text-green-500',
      processing: 'bg-blue-500/10 text-blue-500',
      shipped: 'bg-purple-500/10 text-purple-500',
      delivered: 'bg-green-500/10 text-green-500',
      cancelled: 'bg-red-500/10 text-red-500'
    };
    return colors[status] || 'bg-gray-500/10 text-gray-500';
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/admin/products', label: 'Products', icon: Package },
    { path: '/admin/customers', label: 'Customers', icon: Users },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 glass-strong transform transition-transform duration-300
        lg:translate-x-0 lg:static lg:inset-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#2C1B18]">
            <h1 className="font-heading text-xl">
              <span className="gold-text">Zuiver</span>
              <span className="text-white">Parfum</span>
            </h1>
            <p className="text-[#57534E] text-xs mt-1">Admin Portal</p>
          </div>
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'text-[#A8A29E] hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-[#2C1B18]">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-500/10 w-full transition-colors"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        <header className="glass-strong sticky top-0 z-30 px-4 md:px-6 py-4 flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-white">
            <Menu size={24} />
          </button>
          <h2 className="font-heading text-xl text-white">Orders</h2>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-2 text-white text-sm focus:border-[#D4AF37] focus:outline-none"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </header>

        <div className="p-4 md:p-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="glass rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-[#A8A29E] text-sm border-b border-[#2C1B18]">
                      <th className="text-left py-4 px-4">Order ID</th>
                      <th className="text-left py-4 px-4">Customer</th>
                      <th className="text-left py-4 px-4">Date</th>
                      <th className="text-left py-4 px-4">Items</th>
                      <th className="text-left py-4 px-4">Status</th>
                      <th className="text-left py-4 px-4">Payment</th>
                      <th className="text-right py-4 px-4">Total</th>
                      <th className="text-right py-4 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-[#57534E]">
                          No orders found
                        </td>
                      </tr>
                    ) : (
                      orders.map((order) => (
                        <tr key={order.id} className="border-b border-[#2C1B18]/50 hover:bg-white/5">
                          <td className="py-4 px-4 text-white font-mono text-sm">
                            #{order.id.slice(0, 8)}
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-white">{order.shipping_address?.label || 'N/A'}</p>
                            <p className="text-[#57534E] text-xs">
                              {order.shipping_address?.city}
                            </p>
                          </td>
                          <td className="py-4 px-4 text-[#A8A29E] text-sm">
                            {new Date(order.created_at).toLocaleDateString('nl-NL')}
                          </td>
                          <td className="py-4 px-4 text-[#A8A29E]">
                            {order.items?.length || 0} items
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded text-xs ${getStatusColor(order.payment_status)}`}>
                              {order.payment_status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-right text-[#D4AF37] font-medium">
                            €{order.total.toFixed(2)}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-2 hover:bg-white/10 rounded-lg">
                                  <ChevronDown size={16} className="text-[#A8A29E]" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[#1C1917] border-[#2C1B18]">
                                <DropdownMenuItem 
                                  onClick={() => setSelectedOrder(order)}
                                  className="text-white hover:text-[#D4AF37] hover:bg-[#2C1B18] cursor-pointer"
                                >
                                  <Eye size={16} className="mr-2" /> View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateOrderStatus(order.id, 'processing')}
                                  className="text-white hover:text-[#D4AF37] hover:bg-[#2C1B18] cursor-pointer"
                                >
                                  Mark Processing
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    const tracking = prompt('Enter tracking number:');
                                    if (tracking) updateOrderStatus(order.id, 'shipped', tracking);
                                  }}
                                  className="text-white hover:text-[#D4AF37] hover:bg-[#2C1B18] cursor-pointer"
                                >
                                  <Truck size={16} className="mr-2" /> Mark Shipped
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateOrderStatus(order.id, 'delivered')}
                                  className="text-green-500 hover:text-green-400 hover:bg-[#2C1B18] cursor-pointer"
                                >
                                  Mark Delivered
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => updateOrderStatus(order.id, 'cancelled')}
                                  className="text-red-500 hover:text-red-400 hover:bg-[#2C1B18] cursor-pointer"
                                >
                                  Cancel Order
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Order Detail Modal */}
        {selectedOrder && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="font-heading text-xl text-white">Order Details</h3>
                  <p className="text-[#57534E] text-sm">#{selectedOrder.id}</p>
                </div>
                <button onClick={() => setSelectedOrder(null)} className="text-[#A8A29E] hover:text-white">
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Status */}
                <div className="flex gap-4">
                  <div>
                    <p className="text-[#A8A29E] text-sm">Order Status</p>
                    <span className={`px-3 py-1 rounded text-sm ${getStatusColor(selectedOrder.status)}`}>
                      {selectedOrder.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-[#A8A29E] text-sm">Payment Status</p>
                    <span className={`px-3 py-1 rounded text-sm ${getStatusColor(selectedOrder.payment_status)}`}>
                      {selectedOrder.payment_status}
                    </span>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <p className="text-[#A8A29E] text-sm mb-2">Shipping Address</p>
                  <div className="bg-[#1C1917] rounded-lg p-4">
                    <p className="text-white">{selectedOrder.shipping_address?.label}</p>
                    <p className="text-[#A8A29E]">
                      {selectedOrder.shipping_address?.street} {selectedOrder.shipping_address?.house_number}
                    </p>
                    <p className="text-[#A8A29E]">
                      {selectedOrder.shipping_address?.postal_code} {selectedOrder.shipping_address?.city}
                    </p>
                    <p className="text-[#A8A29E]">{selectedOrder.shipping_address?.country}</p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <p className="text-[#A8A29E] text-sm mb-2">Items</p>
                  <div className="space-y-2">
                    {selectedOrder.items?.map((item, i) => (
                      <div key={i} className="bg-[#1C1917] rounded-lg p-4 flex justify-between">
                        <div>
                          <p className="text-white">{item.name}</p>
                          <p className="text-[#57534E] text-sm">{item.brand} - {item.size_ml}ml × {item.quantity}</p>
                        </div>
                        <p className="text-[#D4AF37]">€{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tracking */}
                {selectedOrder.tracking_number && (
                  <div>
                    <p className="text-[#A8A29E] text-sm">Tracking Number</p>
                    <p className="text-white font-mono">{selectedOrder.tracking_number}</p>
                  </div>
                )}

                {/* Total */}
                <div className="border-t border-[#2C1B18] pt-4 flex justify-between">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-[#D4AF37] text-xl font-bold">
                    €{selectedOrder.total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminOrders;
